import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Shield, Cookie, Gavel, Scale } from "lucide-react";
import { COMPANY } from "../config/COMPANY-INFO";

const NAV = [
  { to: "/pravni/obchodni-podminky", label: "Obchodní podmínky", icon: FileText },
  { to: "/pravni/ochrana-osobnich-udaju", label: "Ochrana osobních údajů", icon: Shield },
  { to: "/pravni/cookies", label: "Cookies", icon: Cookie },
  { to: "/pravni/reklamacni-rad", label: "Reklamační řád", icon: Gavel },
  { to: "/pravni/reseni-sporu", label: "Řešení sporů (ADR)", icon: Scale },
];

/**
 * Sdílený layout pro všechny legislativní stránky. Levá lišta nabídka (lepivá
 * na desktopu, sjede do horního pruhu na mobilu), pravý sloupec je obsah.
 * Stránka má vlastní nadpis a podtitulek navržené pro psaní dlouhých textů
 * (zlatý akcent, dobré řádkování, tmavé pozadí ve stylu zbytku webu).
 */
export default function LegalLayout({ title, subtitle, children, testId }) {
  const { pathname } = useLocation();

  useEffect(() => {
    // Při změně stránky scrollni nahoru
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-apex-bg text-apex-cream" data-testid={testId || "legal-page"}>
      {/* Soft golden glow header */}
      <div className="relative overflow-hidden border-b border-apex-line">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 1000px 280px at 50% -40%, rgba(229,200,128,0.10), transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-10 pb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-apex-cream/65 hover:text-apex-gold transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Zpět na hlavní stránku
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="font-display mt-5 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-apex-cream"
          >
            {title}
          </motion.h1>
          {subtitle && (
            <p className="mt-3 text-apex-cream/65 text-sm md:text-base max-w-2xl">{subtitle}</p>
          )}
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-apex-gold/80">
            <span className="w-1.5 h-1.5 rounded-full bg-apex-gold" />
            Účinnost: {COMPANY.effectiveDate}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[260px_1fr] gap-10">
        {/* Side nav */}
        <aside className="lg:sticky lg:top-28 lg:self-start" data-testid="legal-side-nav">
          <div className="rounded-2xl bg-apex-card border border-apex-line p-4">
            <div className="text-[11px] uppercase tracking-[0.3em] text-apex-gold/80 px-2 pb-2 mb-2 border-b border-apex-line">
              Právní dokumenty
            </div>
            <ul className="space-y-1">
              {NAV.map((n) => {
                const active = pathname === n.to;
                return (
                  <li key={n.to}>
                    <Link
                      to={n.to}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                        active
                          ? "bg-apex-gold text-apex-bg font-semibold"
                          : "text-apex-cream/75 hover:bg-apex-bg hover:text-apex-gold"
                      }`}
                      data-testid={`legal-nav-${n.to.split("/").pop()}`}
                    >
                      <n.icon className="w-4 h-4 flex-shrink-0" />
                      <span>{n.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="mt-4 rounded-2xl bg-apex-bg border border-apex-line p-4 text-xs text-apex-cream/55 leading-relaxed">
            Máte dotaz k některému z dokumentů?<br />
            Napište nám na{" "}
            <a className="text-apex-gold hover:underline break-all" href={`mailto:${COMPANY.contact.email}`}>
              {COMPANY.contact.email}
            </a>
          </div>
        </aside>

        {/* Content */}
        <article className="prose-legal max-w-3xl">
          {children}
        </article>
      </div>
    </div>
  );
}

/**
 * Drobné textové stavebnice použité na všech legal stránkách. Nabízí jednotnou
 * typografii bez nutnosti tahat externí @tailwindcss/typography plugin.
 */
export const LegalSection = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 mt-10 first:mt-0" data-testid={id ? `legal-section-${id}` : undefined}>
    <h2 className="font-display text-2xl md:text-3xl font-bold text-apex-cream mt-0 mb-4">
      <span className="text-apex-gold">§</span> {title}
    </h2>
    <div className="space-y-4 text-apex-cream/85 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export const LegalSub = ({ title, children }) => (
  <div className="mt-5">
    <h3 className="text-apex-cream font-semibold text-lg mb-2">{title}</h3>
    <div className="space-y-3 text-apex-cream/80">{children}</div>
  </div>
);

export const LegalList = ({ children, ordered = false }) => {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`pl-5 space-y-2 text-apex-cream/80 ${ordered ? "list-decimal" : "list-disc marker:text-apex-gold/70"}`}>
      {children}
    </Tag>
  );
};

export const LegalCallout = ({ children, tone = "gold" }) => {
  const styles = {
    gold: "border-apex-gold/40 bg-apex-gold/8 text-apex-cream",
    info: "border-sky-500/30 bg-sky-500/8 text-apex-cream",
    warn: "border-amber-500/40 bg-amber-500/8 text-apex-cream",
  };
  return (
    <div className={`my-5 rounded-xl border ${styles[tone] || styles.gold} px-4 py-3.5 text-[14px] leading-relaxed`}>
      {children}
    </div>
  );
};

// Konzistentní "doplnit" placeholder pro chybějící data
export const Placeholder = ({ field }) => (
  <span
    className="inline-block px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[12px] font-mono tracking-tight"
    title="K doplnění v /app/frontend/src/config/COMPANY-INFO.js"
  >
    [doplnit {field}]
  </span>
);

// Drobný helper: ukazuje hodnotu z COMPANY nebo Placeholder pokud je prázdná
export const Field = ({ value, name }) => {
  if (value && String(value).trim()) return <strong className="text-apex-cream">{value}</strong>;
  return <Placeholder field={name} />;
};
